(() => {
  const formatMoney = window.ThemeUtils?.formatMoney || ((value) => value);

  const safeParse = (node, fallback = []) => {
    try { return JSON.parse(node?.textContent || JSON.stringify(fallback)); }
    catch (error) { console.warn('Unable to parse product data.', error); return fallback; }
  };

  const initTabs = (root) => {
    root.querySelectorAll('[data-product-tabs]').forEach((tabsRoot) => {
      const tabs = [...tabsRoot.querySelectorAll('[data-product-tab]')];
      const panels = [...tabsRoot.querySelectorAll('[data-product-panel]')];
      if (!tabs.length || tabsRoot.dataset.tabsInitialized === 'true') return;
      tabsRoot.dataset.tabsInitialized = 'true';

      const activate = (tab, focus = false) => {
        tabs.forEach((item) => {
          const active = item === tab;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', String(active));
          item.tabIndex = active ? 0 : -1;
        });
        panels.forEach((panel) => {
          const active = panel.id === tab.getAttribute('aria-controls');
          panel.classList.toggle('is-active', active);
          panel.hidden = !active;
        });
        if (focus) tab.focus();
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let nextIndex = index;
          if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = tabs.length - 1;
          activate(tabs[nextIndex], true);
        });
      });
    });
  };

  const initRoot = (root) => {
    if (root.dataset.productInitialized === 'true') return;
    root.dataset.productInitialized = 'true';

    const variants = safeParse(root.querySelector('[data-product-json]'));
    const inventories = safeParse(root.querySelector('[data-product-inventory-json]'));
    if (!variants.length) return;

    const forms = [...root.querySelectorAll('[data-product-form], form[data-ajax-cart-form]')];
    const mediaItems = [...root.querySelectorAll('[data-media-id]')];
    const mediaThumbs = [...root.querySelectorAll('[data-gallery-thumb]')];
    const currentCounter = root.querySelector('[data-gallery-current]');
    const modal = root.querySelector('[data-product-media-modal]');
    const modalImage = root.querySelector('[data-product-media-modal-image]');

    const getOptions = () => [...root.querySelectorAll('[data-variant-picker] fieldset, .variant-picker')]
      .map((fieldset) => fieldset.querySelector('input[type="radio"]:checked')?.value)
      .filter(Boolean);

    const findVariant = () => {
      const options = getOptions();
      if (!options.length) {
        const selectedId = root.querySelector('[data-variant-id], [data-variant-select]')?.value;
        return variants.find((variant) => String(variant.id) === String(selectedId)) || variants[0];
      }
      return variants.find((variant) => variant.options.every((option, index) => option === options[index]));
    };

    const activeMediaIndex = () => Math.max(0, mediaItems.findIndex((item) => item.classList.contains('is-active')));

    const selectMedia = (mediaId, scrollThumb = false) => {
      if (!mediaItems.length) return;
      let selectedIndex = mediaItems.findIndex((item) => String(item.dataset.mediaId) === String(mediaId));
      if (selectedIndex < 0) selectedIndex = 0;
      const selectedItem = mediaItems[selectedIndex];

      mediaItems.forEach((item, index) => {
        const active = index === selectedIndex;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-hidden', String(!active));
        if (!active) item.querySelectorAll('video').forEach((video) => video.pause());
      });
      mediaThumbs.forEach((thumb) => {
        const active = String(thumb.dataset.targetMedia) === String(selectedItem.dataset.mediaId);
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-selected', String(active));
        if (active && scrollThumb) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      });
      if (currentCounter) currentCounter.textContent = String(selectedIndex + 1);
    };

    const moveMedia = (direction) => {
      if (mediaItems.length < 2) return;
      const nextIndex = (activeMediaIndex() + direction + mediaItems.length) % mediaItems.length;
      selectMedia(mediaItems[nextIndex].dataset.mediaId, true);
    };

    const update = (variant) => {
      root.currentVariant = variant;
      forms.forEach((form) => {
        const id = form.querySelector('[name="id"]');
        if (id && variant) id.value = variant.id;
      });

      root.querySelectorAll('[data-product-price]').forEach((node) => { node.textContent = variant ? formatMoney(variant.price) : ''; });
      root.querySelectorAll('[data-product-compare-price]').forEach((node) => {
        const onSale = Boolean(variant && variant.compare_at_price > variant.price);
        node.hidden = !onSale;
        node.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
        node.closest('[data-price-wrapper]')?.classList.toggle('price--sale', onSale);
      });
      root.querySelectorAll('[data-sale-badge]').forEach((node) => { node.hidden = !(variant && variant.compare_at_price > variant.price); });

      root.querySelectorAll('[data-add-to-cart], [data-sticky-add-button]').forEach((button) => {
        button.disabled = !variant?.available;
        const text = button.querySelector('[data-add-to-cart-text]');
        if (text) text.textContent = !variant ? window.theme.strings.unavailable : variant.available ? window.theme.strings.addToCart : window.theme.strings.soldOut;
      });
      root.querySelectorAll('[data-button-price],[data-sticky-price]').forEach((node) => { node.textContent = variant ? formatMoney(variant.price) : ''; });
      root.querySelectorAll('[data-sticky-variant-title]').forEach((node) => { node.textContent = variant?.title || ''; });
      root.querySelectorAll('[data-product-sku]').forEach((node) => { node.textContent = variant?.sku || '—'; });

      const inventory = inventories.find((item) => String(item.id) === String(variant?.id));
      const inventoryText = root.querySelector('[data-inventory-text]');
      const inventoryWrap = root.querySelector('[data-inventory-status]');
      if (inventoryText && inventoryWrap && variant) {
        const threshold = Number(getComputedStyle(document.documentElement).getPropertyValue('--low-inventory-threshold') || 5);
        const low = Boolean(inventory?.management && inventory.quantity <= threshold && inventory.quantity > 0);
        const lowTemplate = inventoryWrap.dataset.lowStockTemplate || 'Only __COUNT__ left';
        inventoryText.textContent = !variant.available
          ? (inventoryWrap.dataset.outOfStockText || 'Out of stock')
          : low
            ? lowTemplate.replace('__COUNT__', inventory.quantity)
            : (inventoryWrap.dataset.inStockText || 'In stock');
        inventoryWrap.classList.toggle('inventory-status--low', low);
      }

      if (variant?.featured_media?.id) selectMedia(variant.featured_media.id, true);
      if (variant && !root.closest('[data-quick-view-modal]')) history.replaceState({}, '', `${root.dataset.productUrl}?variant=${variant.id}`);
    };

    root.addEventListener('change', (event) => {
      if (event.target.matches('[data-option-value], .variant-picker input[type="radio"]')) {
        const fieldset = event.target.closest('fieldset');
        fieldset?.querySelectorAll('.option-button,.swatch').forEach((label) => label.classList.toggle('is-selected', label.contains(event.target) && event.target.checked));
        const selected = fieldset?.querySelector('[data-selected-option]');
        if (selected) selected.textContent = event.target.value;
        update(findVariant());
      }

      if (event.target.matches('[data-product-quantity]')) {
        const value = Math.max(1, Number.parseInt(event.target.value, 10) || 1);
        event.target.value = String(value);
        forms.forEach((form) => {
          const hidden = form.querySelector('[data-form-quantity]');
          if (hidden) hidden.value = String(value);
        });
      }
    });

    root.addEventListener('click', async (event) => {
      const thumb = event.target.closest('[data-gallery-thumb]');
      if (thumb) selectMedia(thumb.dataset.targetMedia);
      if (event.target.closest('[data-gallery-previous]')) moveMedia(-1);
      if (event.target.closest('[data-gallery-next]')) moveMedia(1);

      const quantityInput = root.querySelector('[data-product-quantity]');
      if (quantityInput && (event.target.closest('[data-quantity-minus]') || event.target.closest('[data-quantity-plus]'))) {
        const change = event.target.closest('[data-quantity-plus]') ? 1 : -1;
        quantityInput.value = String(Math.max(1, (Number.parseInt(quantityInput.value, 10) || 1) + change));
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (event.target.closest('[data-sticky-add-button]')) root.querySelector('[data-product-form] [data-add-to-cart]')?.click();

      if (event.target.closest('[data-gallery-zoom]') && modal && modalImage) {
        const activeImage = mediaItems[activeMediaIndex()]?.querySelector('img');
        if (!activeImage) return;
        modalImage.src = activeImage.currentSrc || activeImage.src;
        modalImage.alt = activeImage.alt || '';
        if (typeof modal.showModal === 'function') modal.showModal();
      }
      if (event.target.closest('[data-product-media-modal-close]')) modal?.close();

      const shareButton = event.target.closest('[data-product-share]');
      if (shareButton) {
        const shareData = { title: shareButton.dataset.shareTitle, url: shareButton.dataset.shareUrl };
        const feedback = root.querySelector('[data-share-feedback]');
        try {
          if (navigator.share) await navigator.share(shareData);
          else await navigator.clipboard.writeText(shareData.url);
          if (feedback) {
            feedback.hidden = false;
            window.setTimeout(() => { feedback.hidden = true; }, 2200);
          }
        } catch (error) {
          if (error?.name !== 'AbortError') console.warn('Product share failed.', error);
        }
      }
    });

    modal?.addEventListener('click', (event) => { if (event.target === modal) modal.close(); });

    const mainButton = root.querySelector('[data-product-form] [data-add-to-cart]');
    const sticky = root.querySelector('[data-sticky-add-to-cart]');
    if (mainButton && sticky && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => sticky.classList.toggle('is-visible', !entry.isIntersecting), { threshold: 0 }).observe(mainButton);
    }

    const initialActive = mediaItems.find((item) => item.classList.contains('is-active')) || mediaItems[0];
    if (initialActive) selectMedia(initialActive.dataset.mediaId);
    initTabs(root);
    update(findVariant());
  };

  const initAll = (scope = document) => scope.querySelectorAll('[data-product-root]').forEach(initRoot);
  window.initProductRoots = initAll;
  document.addEventListener('DOMContentLoaded', () => initAll());
})();
